import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import {
  normalizeCompanyDocSnapshot,
  buildBillPreviewCompanyDetails,
  buildBillPreviewBankDetails,
  getCollectionForCompany,
} from '../utils/dataAdapter';
import { preloadBillImages } from '../utils/imageUtils';
import { withMPINProtection } from './MPINProtectedRoute';
import {
  LAYOUT_DOC_TYPES,
  LAYOUT_BILL_SOURCES,
  getTemplatesForDocType,
  resolveTemplateComponent,
  getDefaultTemplateForDocType,
  createEmptyRow,
  createDefaultLayoutState,
  recalculateRowAmount,
  buildBillDataFromLayout,
  buildPartyDetailsFromLayout,
  saveLayoutDraft,
  loadLayoutDraft,
  clearLayoutDraft,
  mapBillToLayoutState,
  filterBillsForSync,
  pickBillFromLookup,
  getBillPartyName,
  getBillDisplayDate,
  getBillDisplayAmount,
} from '../utils/layoutDocumentUtils';
import {
  getBillTemplateRoot,
  buildPdfBlobFromElement,
  openPdfPreviewWindow,
  downloadPdfBlob,
} from '../utils/billPdfExport';
import { printBillFromContainer, prepareBillPrintBodyHtml } from '../utils/billPrintExport';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const WATERMARK_STYLE = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  zIndex: 20,
  overflow: 'hidden',
};

const WATERMARK_TEXT_STYLE = {
  fontSize: '22px',
  fontWeight: 700,
  color: 'rgba(180, 40, 40, 0.18)',
  transform: 'rotate(-32deg)',
  textAlign: 'center',
  lineHeight: 1.4,
  letterSpacing: '0.04em',
  userSelect: 'none',
  maxWidth: '90%',
};

function LayoutDocumentBuilder({ db, userId, isAuthReady, appId }) {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  const previewRef = useRef(null);
  const billListRef = useRef(null);
  const [company, setCompany] = useState({});
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [existingBills, setExistingBills] = useState([]);
  const [syncSourceKey, setSyncSourceKey] = useState('invoice');
  const [syncSearch, setSyncSearch] = useState('');
  const [syncBillNumber, setSyncBillNumber] = useState('');
  const [syncFromDate, setSyncFromDate] = useState('');
  const [syncToDate, setSyncToDate] = useState('');
  const [selectedBillId, setSelectedBillId] = useState('');
  const [billsLoading, setBillsLoading] = useState(false);
  const [syncLookupMessage, setSyncLookupMessage] = useState('');
  const [layout, setLayout] = useState(() => createDefaultLayoutState());
  const [exporting, setExporting] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  const companyDetails = useMemo(
    () => buildBillPreviewCompanyDetails(company, currentCompany),
    [company, currentCompany]
  );
  const bankDetails = useMemo(
    () => buildBillPreviewBankDetails(company, currentCompany),
    [company, currentCompany]
  );
  const billData = useMemo(() => buildBillDataFromLayout(layout), [layout]);
  const partyDetails = useMemo(() => buildPartyDetailsFromLayout(layout), [layout]);
  const TemplateComponent = useMemo(
    () => resolveTemplateComponent(layout.templateKey, layout.docType),
    [layout.templateKey, layout.docType]
  );
  const docTypeMeta = LAYOUT_DOC_TYPES.find((d) => d.key === layout.docType) || LAYOUT_DOC_TYPES[0];
  const availableTemplates = getTemplatesForDocType(layout.docType);
  const billSyncResults = useMemo(
    () =>
      filterBillsForSync(existingBills, {
        search: syncSearch,
        billNumber: syncBillNumber,
        fromDate: syncFromDate,
        toDate: syncToDate,
        parties,
      }),
    [existingBills, syncSearch, syncBillNumber, syncFromDate, syncToDate, parties]
  );
  const filteredBills = billSyncResults.bills;
  const selectedBill = useMemo(
    () => existingBills.find((b) => b.id === selectedBillId) || null,
    [existingBills, selectedBillId]
  );

  useEffect(() => {
    if (!companyId) return;
    const draft = loadLayoutDraft(companyId);
    if (draft) setLayout(draft);
  }, [companyId]);

  useEffect(() => {
    const cid = companyId;
    if (!db || !isAuthReady || !cid) return;
    const companyDocRef = doc(db, 'companies', cid);
    const unsubscribe = onSnapshot(companyDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setCompany(normalizeCompanyDocSnapshot(docSnap.data() || {}, cid));
      }
    });
    return () => unsubscribe();
  }, [db, isAuthReady, companyId]);

  useEffect(() => {
    if (!db || !userId || !isAuthReady || !companyId) return;
    const partiesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
    if (!partiesRef) return;
    const unsubscribe = onSnapshot(partiesRef, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setParties(list);
    });
    return () => unsubscribe();
  }, [db, userId, isAuthReady, companyId, appId]);

  useEffect(() => {
    if (!db || !userId || !isAuthReady || !companyId) return;
    const itemsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
    if (!itemsRef) return;
    const unsubscribe = onSnapshot(itemsRef, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setItems(list);
    });
    return () => unsubscribe();
  }, [db, userId, isAuthReady, companyId, appId]);

  useEffect(() => {
    if (!db || !userId || !isAuthReady || !companyId) return;
    const source = LAYOUT_BILL_SOURCES.find((s) => s.key === syncSourceKey);
    if (!source) return;
    const billsRef = getCollectionForCompany(db, { companyId, appId, userId }, source.collection);
    if (!billsRef) return;
    setBillsLoading(true);
    const unsubscribe = onSnapshot(
      billsRef,
      (snap) => {
        const list = [];
        snap.forEach((d) => {
          const data = { id: d.id, ...d.data() };
          if (source.key === 'invoice') {
            const dt = data.docType;
            if (dt && dt !== 'invoice') return;
          }
          list.push(data);
        });
        setExistingBills(list);
        setBillsLoading(false);
      },
      () => {
        setExistingBills([]);
        setBillsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [db, userId, isAuthReady, companyId, appId, syncSourceKey]);

  useEffect(() => {
    setSelectedBillId('');
    setSyncSearch('');
    setSyncBillNumber('');
    setSyncFromDate('');
    setSyncToDate('');
    setSyncLookupMessage('');
  }, [syncSourceKey]);

  const scrollBillIntoView = useCallback((billId) => {
    requestAnimationFrame(() => {
      const row = billListRef.current?.querySelector(`[data-bill-id="${billId}"]`);
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }, []);

  const selectBill = useCallback(
    (bill) => {
      if (!bill?.id) return;
      setSelectedBillId(bill.id);
      setSyncLookupMessage('');
      scrollBillIntoView(bill.id);
    },
    [scrollBillIntoView]
  );

  const handleFindBillNumber = useCallback(() => {
    const q = syncBillNumber.trim();
    if (!q) {
      setSyncLookupMessage('Enter a bill number to find.');
      return;
    }
    const found = pickBillFromLookup(existingBills, q, parties);
    if (!found) {
      setSelectedBillId('');
      setSyncLookupMessage(`No document matching "${q}".`);
      return;
    }
    selectBill(found);
    const exact = String(found.number || '').toLowerCase() === q.toLowerCase();
    setSyncLookupMessage(
      exact
        ? `Selected ${found.number}.`
        : `Selected closest match: ${found.number}.`
    );
  }, [syncBillNumber, existingBills, parties, selectBill]);

  const updateLayout = useCallback((patch) => {
    setLayout((prev) => ({ ...prev, ...patch }));
    setDraftSaved(false);
  }, []);

  const handleDocTypeChange = (docType) => {
    setSyncSourceKey(docType);
    updateLayout({
      docType,
      templateKey: getDefaultTemplateForDocType(docType),
    });
  };

  const handleSyncSourceChange = (sourceKey) => {
    setSyncSourceKey(sourceKey);
  };

  const clearSyncFilters = () => {
    setSyncSearch('');
    setSyncBillNumber('');
    setSyncFromDate('');
    setSyncToDate('');
    setSelectedBillId('');
    setSyncLookupMessage('');
  };

  const handleRowChange = (rowId, field, value) => {
    setLayout((prev) => {
      const rows = prev.rows.map((row) => {
        if (row.id !== rowId) return row;
        const updated = { ...row, [field]: value };
        if (field === 'qty' || field === 'rate') {
          return recalculateRowAmount(updated);
        }
        return updated;
      });
      return { ...prev, rows };
    });
    setDraftSaved(false);
  };

  const addRow = () => {
    setLayout((prev) => ({ ...prev, rows: [...prev.rows, createEmptyRow()] }));
    setDraftSaved(false);
  };

  const removeRow = (rowId) => {
    setLayout((prev) => {
      const rows = prev.rows.filter((r) => r.id !== rowId);
      return { ...prev, rows: rows.length ? rows : [createEmptyRow()] };
    });
    setDraftSaved(false);
  };

  const layoutHasContent = useCallback(() => {
    if (layout.documentNumber?.trim()) return true;
    if (layout.partyName?.trim()) return true;
    return (layout.rows || []).some(
      (row) =>
        row.description?.trim() ||
        row.qty ||
        row.rate ||
        row.amount
    );
  }, [layout]);

  const handleSyncFromExistingBill = (billIdOverride) => {
    const targetId = billIdOverride || selectedBillId;
    if (!targetId) {
      alert('Please select a document from the list or use Find by bill number.');
      return;
    }
    const bill = existingBills.find((b) => b.id === targetId);
    if (!bill) {
      alert('Selected document not found. Try refreshing the list.');
      return;
    }
    if (layoutHasContent()) {
      const ok = window.confirm(
        'This will replace the current form with data from the selected document. You can still edit number and date afterward. Continue?'
      );
      if (!ok) return;
    }
    const templateFitsSource = getTemplatesForDocType(syncSourceKey).some(
      (t) => t.key === layout.templateKey
    );
    const next = mapBillToLayoutState(bill, {
      parties,
      items,
      sourceKey: syncSourceKey,
      preserve: {
        templateKey: templateFitsSource ? layout.templateKey : undefined,
        showWatermark: layout.showWatermark,
      },
    });
    setLayout(next);
    setDraftSaved(false);
  };

  const handlePartyPrefill = (partyId) => {
    const party = parties.find((p) => p.id === partyId);
    if (!party) return;
    updateLayout({
      partyName: party.firmName || party.name || '',
      partyAddress: party.address || '',
      partyGstin: party.gstin || party.gstNumber || '',
      partyContact: party.contactNumber || party.phone || '',
      partyState: party.state || '',
    });
  };

  const handleSaveDraft = () => {
    if (!companyId) return;
    saveLayoutDraft(companyId, layout);
    setDraftSaved(true);
  };

  const handleClearDraft = () => {
    if (!companyId) return;
    if (!window.confirm('Clear saved draft and reset the form?')) return;
    clearLayoutDraft(companyId);
    setLayout(createDefaultLayoutState());
    setDraftSaved(false);
  };

  const handleReset = () => {
    if (!window.confirm('Reset all fields to defaults?')) return;
    setLayout(createDefaultLayoutState({ docType: layout.docType, templateKey: layout.templateKey }));
    setDraftSaved(false);
  };

  const buildFilename = () => {
    const fmt = (d) => {
      try {
        const x = new Date(d);
        const day = String(x.getDate());
        const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][x.getMonth()];
        return `${day}${mon}${x.getFullYear()}`;
      } catch {
        return 'draft';
      }
    };
    const num = layout.documentNumber || 'DRAFT';
    return `LAYOUT_${docTypeMeta.label.toUpperCase().replace(/\s+/g, '_')}_${num}_${fmt(layout.documentDate)}.pdf`;
  };

  const handleExportPdf = async (mode = 'preview') => {
    if (!previewRef.current) return;
    setExporting(true);
    try {
      await preloadBillImages(companyDetails, bankDetails);
      const bodyHtml = await prepareBillPrintBodyHtml(previewRef.current);
      const temp = document.createElement('div');
      temp.innerHTML = bodyHtml;
      const exportRoot = getBillTemplateRoot(temp);
      if (!exportRoot) throw new Error('Template preview not found');
      document.body.appendChild(temp);
      temp.style.position = 'fixed';
      temp.style.left = '-10000px';
      temp.style.top = '0';
      try {
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        const pdfBlob = await buildPdfBlobFromElement(exportRoot);
        const filename = buildFilename();
        if (mode === 'download') {
          downloadPdfBlob(pdfBlob, filename);
        } else {
          openPdfPreviewWindow(pdfBlob, filename);
        }
      } finally {
        temp.remove();
      }
    } catch (err) {
      console.error('Layout PDF export failed:', err);
      alert('Could not generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = async () => {
    if (!previewRef.current) return;
    setExporting(true);
    try {
      await preloadBillImages(companyDetails, bankDetails);
      await printBillFromContainer(previewRef.current, { title: 'Dummy Bill' });
    } catch (err) {
      console.error('Layout print failed:', err);
      alert(err.message || 'Could not print. Please allow popups and try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="layout-doc-no-print bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link
              to="/bill-templates"
              className="inline-flex items-center text-sm text-slate-600 hover:text-blue-600 mb-1"
            >
              ← Back to Bill Templates
            </Link>
            <h1 className="text-xl font-bold text-slate-900">Dummy Bill</h1>
            <p className="text-xs text-amber-700 mt-0.5">
              Under Bill Templates · for reference &amp; printing only — not saved to accounts.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSaveDraft}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 bg-white hover:bg-slate-50"
            >
              {draftSaved ? 'Draft saved' : 'Save draft'}
            </button>
            <button
              type="button"
              onClick={() => handleExportPdf('preview')}
              disabled={exporting}
              className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {exporting ? 'Generating…' : 'PDF'}
            </button>
            <button
              type="button"
              onClick={() => handleExportPdf('download')}
              disabled={exporting}
              className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Download
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={exporting}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              {exporting ? 'Preparing…' : 'Print'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto p-4 grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="layout-doc-no-print space-y-4">
          <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 space-y-3">
            <h2 className="font-semibold text-slate-800">Document type</h2>
            <div className="flex flex-wrap gap-2">
              {LAYOUT_DOC_TYPES.map((dt) => (
                <button
                  key={dt.key}
                  type="button"
                  onClick={() => handleDocTypeChange(dt.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                    layout.docType === dt.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {dt.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <label className="block text-sm">
                <span className="text-slate-600">Template</span>
                <AppSelect
                  value={layout.templateKey}
                  onChange={(e) => updateLayout({ templateKey: e.target.value })}
                  aria-label="Template"
                  sheetTitle="Template"
                  options={availableTemplates.map((t) => ({ value: t.key, label: t.label }))}
                  triggerClassName={`${APP_SELECT_FILTER_TRIGGER} mt-1 rounded-md px-2 py-1.5`}
                />
              </label>
              <label className="flex items-center gap-2 text-sm pt-5">
                <input
                  type="checkbox"
                  checked={layout.showWatermark}
                  onChange={(e) => updateLayout({ showWatermark: e.target.checked })}
                />
                <span>Show &quot;For reference only&quot; watermark</span>
              </label>
            </div>
          </section>

          <section className="bg-white rounded-lg shadow-sm border border-blue-100 p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className="font-semibold text-slate-800">Load from existing document</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Copy header, party, and line items from a saved invoice, quotation, or challan. You can change the date and number after loading.
                </p>
              </div>
              {layout.syncedFromNumber && (
                <span className="text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                  Loaded from {layout.syncedFromNumber}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {LAYOUT_BILL_SOURCES.map((src) => (
                <button
                  key={src.key}
                  type="button"
                  onClick={() => handleSyncSourceChange(src.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                    syncSourceKey === src.key
                      ? 'bg-slate-800 text-white border-slate-800'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {src.label}
                </button>
              ))}
            </div>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="block text-sm flex-1">
                  <span className="text-slate-600 font-medium">Find by bill number</span>
                  <div className="mt-1 flex gap-2">
                    <input
                      type="text"
                      className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                      placeholder="e.g. INV25-26/12"
                      value={syncBillNumber}
                      onChange={(e) => {
                        setSyncBillNumber(e.target.value);
                        setSyncLookupMessage('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleFindBillNumber();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleFindBillNumber}
                      disabled={billsLoading}
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 shrink-0"
                    >
                      Find
                    </button>
                  </div>
                </label>
              </div>

              <label className="block text-sm">
                <span className="text-slate-600">Search party or partial number</span>
                <input
                  type="text"
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  placeholder="Party name or part of bill number"
                  value={syncSearch}
                  onChange={(e) => setSyncSearch(e.target.value)}
                />
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block text-sm">
                  <span className="text-slate-600">From date</span>
                  <input
                    type="date"
                    className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                    value={syncFromDate}
                    onChange={(e) => setSyncFromDate(e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-slate-600">To date</span>
                  <input
                    type="date"
                    className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                    value={syncToDate}
                    onChange={(e) => setSyncToDate(e.target.value)}
                  />
                </label>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                <span>
                  {billsLoading
                    ? 'Loading documents…'
                    : billSyncResults.isFiltered
                      ? `Showing ${filteredBills.length} of ${billSyncResults.totalMatches} match${billSyncResults.totalMatches === 1 ? '' : 'es'}`
                      : `Showing ${filteredBills.length} most recent of ${existingBills.length} — search or use Find to narrow`}
                </span>
                {(syncSearch || syncBillNumber || syncFromDate || syncToDate) && (
                  <button
                    type="button"
                    onClick={clearSyncFilters}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {syncLookupMessage && (
                <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
                  {syncLookupMessage}
                </p>
              )}

              <div
                ref={billListRef}
                className="border border-slate-200 rounded-md overflow-hidden max-h-72 overflow-y-auto bg-white"
              >
                {billsLoading ? (
                  <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
                ) : filteredBills.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">
                    No documents match your search. Try a different number or party name.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                      <tr>
                        <th className="text-left font-medium text-slate-600 px-3 py-2">Number</th>
                        <th className="text-left font-medium text-slate-600 px-3 py-2 hidden sm:table-cell">Party</th>
                        <th className="text-left font-medium text-slate-600 px-3 py-2">Date</th>
                        <th className="text-right font-medium text-slate-600 px-3 py-2 hidden xs:table-cell">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBills.map((bill) => {
                        const isSelected = bill.id === selectedBillId;
                        return (
                          <tr
                            key={bill.id}
                            data-bill-id={bill.id}
                            onClick={() => selectBill(bill)}
                            onDoubleClick={() => {
                              selectBill(bill);
                              handleSyncFromExistingBill(bill.id);
                            }}
                            className={`cursor-pointer border-b border-slate-100 last:border-0 transition-colors ${
                              isSelected
                                ? 'bg-blue-50 hover:bg-blue-100'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <td className="px-3 py-2.5 font-medium text-slate-900 whitespace-nowrap">
                              {bill.number || '—'}
                            </td>
                            <td className="px-3 py-2.5 text-slate-700 hidden sm:table-cell max-w-[140px] truncate" title={getBillPartyName(bill, parties)}>
                              {getBillPartyName(bill, parties)}
                            </td>
                            <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                              {getBillDisplayDate(bill)}
                            </td>
                            <td className="px-3 py-2.5 text-slate-700 text-right whitespace-nowrap hidden sm:table-cell">
                              {getBillDisplayAmount(bill)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {selectedBill && (
                <div className="text-xs bg-blue-50 border border-blue-100 rounded-md px-3 py-2 text-blue-900">
                  Selected: <strong>{selectedBill.number}</strong>
                  {' · '}
                  {getBillPartyName(selectedBill, parties)}
                  {' · '}
                  {getBillDisplayDate(selectedBill)}
                  <span className="text-blue-700 ml-1">(double-click row to load immediately)</span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleSyncFromExistingBill()}
              disabled={!selectedBillId || billsLoading}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Load into layout
            </button>
          </section>

          <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 space-y-3">
            <h2 className="font-semibold text-slate-800">Header</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-slate-600">{docTypeMeta.numberLabel}</span>
                <input
                  type="text"
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  placeholder="e.g. INV25-26/042"
                  value={layout.documentNumber}
                  onChange={(e) => updateLayout({ documentNumber: e.target.value })}
                />
              </label>
              <label className="block text-sm">
                <span className="text-slate-600">Date (any date allowed)</span>
                <input
                  type="date"
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  value={layout.documentDate}
                  onChange={(e) => updateLayout({ documentDate: e.target.value })}
                />
              </label>
            </div>
          </section>

          <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold text-slate-800">Party (free text)</h2>
              {parties.length > 0 && (
                <AppSelect
                  value=""
                  onChange={(e) => {
                    if (e.target.value) handlePartyPrefill(e.target.value);
                  }}
                  placeholder="Prefill from party…"
                  aria-label="Prefill from party"
                  sheetTitle="Prefill from party"
                  options={parties.map((p) => ({
                    value: p.id,
                    label: p.firmName || p.name || p.id,
                  }))}
                  triggerClassName={`${APP_SELECT_FILTER_TRIGGER} w-auto min-w-[12rem] text-sm border-slate-300 rounded-md px-2 py-1`}
                />
              )}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <input
                type="text"
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                placeholder="Party / customer name"
                value={layout.partyName}
                onChange={(e) => updateLayout({ partyName: e.target.value })}
              />
              <textarea
                rows={2}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                placeholder="Address"
                value={layout.partyAddress}
                onChange={(e) => updateLayout({ partyAddress: e.target.value })}
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  placeholder="GSTIN"
                  value={layout.partyGstin}
                  onChange={(e) => updateLayout({ partyGstin: e.target.value })}
                />
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  placeholder="Contact"
                  value={layout.partyContact}
                  onChange={(e) => updateLayout({ partyContact: e.target.value })}
                />
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  placeholder="State"
                  value={layout.partyState}
                  onChange={(e) => updateLayout({ partyState: e.target.value })}
                />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Line items</h2>
              <button
                type="button"
                onClick={addRow}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add row
              </button>
            </div>
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              {layout.rows.map((row, idx) => (
                <div key={row.id} className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-slate-500">Row {idx + 1}</span>
                    {layout.rows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      className="sm:col-span-2 border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="Description"
                      value={row.description}
                      onChange={(e) => handleRowChange(row.id, 'description', e.target.value)}
                    />
                    <input
                      type="text"
                      className="border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="HSN/SAC"
                      value={row.hsn}
                      onChange={(e) => handleRowChange(row.id, 'hsn', e.target.value)}
                    />
                    <input
                      type="number"
                      min="0"
                      className="border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="Qty"
                      value={row.qty}
                      onChange={(e) => handleRowChange(row.id, 'qty', e.target.value)}
                    />
                    <input
                      type="number"
                      min="0"
                      className="border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="Rate"
                      value={row.rate}
                      onChange={(e) => handleRowChange(row.id, 'rate', e.target.value)}
                    />
                    <input
                      type="number"
                      min="0"
                      className="border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="Amount"
                      value={row.amount}
                      onChange={(e) => handleRowChange(row.id, 'amount', e.target.value)}
                    />
                    <input
                      type="number"
                      min="0"
                      className="border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="SGST %"
                      value={row.sgst}
                      onChange={(e) => handleRowChange(row.id, 'sgst', e.target.value)}
                    />
                    <input
                      type="number"
                      min="0"
                      className="border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="CGST %"
                      value={row.cgst}
                      onChange={(e) => handleRowChange(row.id, 'cgst', e.target.value)}
                    />
                    <input
                      type="number"
                      min="0"
                      className="border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="IGST %"
                      value={row.igst}
                      onChange={(e) => handleRowChange(row.id, 'igst', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
              <label className="text-sm">
                <span className="text-slate-600">Discount type</span>
                <AppSelect
                  value={layout.discountType}
                  onChange={(e) => updateLayout({ discountType: e.target.value })}
                  aria-label="Discount type"
                  sheetTitle="Discount type"
                  options={[
                    { value: 'percent', label: 'Percent' },
                    { value: 'amount', label: 'Amount' },
                  ]}
                  triggerClassName={`${APP_SELECT_FILTER_TRIGGER} mt-1 rounded-md px-2 py-1`}
                />
              </label>
              <label className="text-sm">
                <span className="text-slate-600">Discount value</span>
                <input
                  type="number"
                  min="0"
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1 text-sm"
                  value={layout.discountValue}
                  onChange={(e) => updateLayout({ discountValue: e.target.value })}
                />
              </label>
            </div>
            <textarea
              rows={2}
              className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
              placeholder="Notes / terms"
              value={layout.notes}
              onChange={(e) => updateLayout({ notes: e.target.value })}
            />
          </section>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Reset form
            </button>
            <button
              type="button"
              onClick={handleClearDraft}
              className="px-3 py-1.5 text-sm rounded-md border border-red-200 text-red-700 hover:bg-red-50"
            >
              Clear saved draft
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="xl:sticky xl:top-20 xl:self-start">
          <div className="layout-doc-no-print text-xs text-slate-500 mb-2 text-center">
            Live preview — company logo &amp; bank details from your profile
          </div>
          <div
            id="layout-doc-print-area"
            ref={previewRef}
            className="relative bg-white rounded-lg shadow-md overflow-visible"
          >
            {layout.showWatermark && (
              <div style={WATERMARK_STYLE} aria-hidden="true">
                <div style={WATERMARK_TEXT_STYLE}>
                  FOR REFERENCE ONLY
                  <br />
                  NOT RECORDED IN ACCOUNTS
                </div>
              </div>
            )}
            <TemplateComponent
              billData={billData}
              companyDetails={companyDetails}
              partyDetails={partyDetails}
              bankDetails={bankDetails}
              pageSize="a4"
              orientation="portrait"
              previewMode={false}
              sharePreview={true}
              showAmountsByDefault={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default withMPINProtection(LayoutDocumentBuilder, 'Bill Templates');
